import React from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import { get, omit } from 'lodash';
import { Grid } from '@material-ui/core';
import { Journey } from '../../_common/Journey';
import {
  SLACK_INTEGRATION_JOURNEY_KEY, SLACK_INTEGRATION_JOURNEY_EDIT_KEY,
} from '../../../../router';
import { EditSlackIntegrationStoreWrapper } from '../store';
import {
  PauseModal, PauseModalBody, PauseModalFooter, PauseModalTitle,
} from '../../../components/PauseModal';
import { PauseButton } from '../../../components/PauseButton';
import { PauseForm } from '../../../components/PauseForm';
import { PauseSubmitButton } from '../../../components/PauseSubmitButton';
import { PauseLoader } from '../../../components/PauseLoader';
import { PauseFormSelect } from '../../../components/PauseFormField2/PauseFormSelect';
import { PauseFormTextField } from '../../../components/PauseFormField2/PauseFormTextField';
import { PauseInteractiveButton } from '../../../components/PauseInteractiveButton';
import { PauseTypography } from '../../../components/PauseTypography';
import { Info } from '../../../../assets/icons/feather/components';
import { PauseFormAutocomplete } from '../../../components/PauseFormField2/PauseFormAutocomplete';
import { PauseFormField2 } from '../../../components/PauseFormField2';
import { PauseCheckbox } from '../../../components/PauseCheckbox';

const hours = Array.from({
  length: 48,
}, (_, hour) => {
  const time = moment({
    hour: Math.floor(hour / 2),
    minutes: (hour % 2 === 0 ? 0 : 30),
  });
  return {
    value: time.format('HH:mm:ss'),
    label: time.format('h:mm a'),
  };
});

const daysOfWeek = moment.weekdays(true);

export const EditSlackIntegrationJourney = () => (
  <Journey
    journey={SLACK_INTEGRATION_JOURNEY_KEY}
    stop={SLACK_INTEGRATION_JOURNEY_EDIT_KEY}
  >
    <EditSlackIntegrationStoreWrapper>
      {({
        onClose,
        integration,
        done,
        teams,
        slackChannels,
        onEditIntegration,
        onDeleteSlackIntegration,
        onCreateSlackIntegration,
        type,
      }) => {
        const allTeams = {
          value: 'all',
          label: 'All teams',
        };
        const defaultValue = {
          teamID: allTeams,
          notificationTime: moment({ hour: 8, minutes: 0 }).format('HH:mm:ss'),
          slackChannelID: slackChannels.length ? {
            value: get(slackChannels, '0.id'),
            label: get(slackChannels, '0.name'),
          } : null,
          ...(type === 'weekly' && { notificationDayOfWeek: daysOfWeek[0].toUpperCase() }),
          showLeaveType: true,
        };
        const getLabelValueFromTeamID = (teamID) => (!teamID ? allTeams : {
          label: (teams || []).find((t) => t.id === teamID)?.name,
          value: teamID,
        });
        const getChannelName = (id) => get(slackChannels.find((sc) => sc.id === id), 'name', 'Private channel');
        return (
          <PauseModal onClose={onClose} open>
            {!done ? (<PauseLoader showPageTitle={false} />) : (
              <PauseForm
                initialValues={integration ? {
                  ...integration,
                  slackChannelID: {
                    label: getChannelName(integration?.slackChannelID),
                    value: integration?.slackChannelID,
                  },
                  teamID: getLabelValueFromTeamID(integration?.teamID),
                } : defaultValue}
                validationSchema={Yup.object({
                  notificationTime: Yup.string().required(),
                  teamID: Yup.string().required(),
                  ...(get(integration, 'deprecated', false) && {
                    channelName: Yup.string().required(),
                  }),
                  ...(!get(integration, 'deprecated') && {
                    slackChannelID: Yup.string().required(),
                  }),
                  ...(type === 'weekly' && {
                    notificationDayOfWeek: Yup.string().required(),
                  }),
                })}
                onSubmit={(values) => {
                  const formattedValues = {
                    ...omit(values, ['teamID', 'slackChannelID']),
                    teamID: get(values, 'teamID.value'),
                    slackChannelID: get(values, 'slackChannelID.value'),
                  };
                  if (integration) return onEditIntegration(formattedValues);
                  return onCreateSlackIntegration(formattedValues);
                }}
              >
                <PauseModalTitle
                  title={integration ? `Edit ${integration?.type} summary` : `Add ${type} summary`}
                  onClose={onClose}
                />
                <PauseModalBody>
                  <div css={(theme) => ({
                    display: 'grid',
                    gridGap: theme.spacing(3),
                  })}
                  >
                    <PauseFormAutocomplete
                      name="teamID"
                      fieldProps={{
                        label: 'Team',
                        options: [allTeams].concat(teams.map((t) => ({
                          label: t.name,
                          value: t.id,
                        }))),
                      }}
                    />
                    {get(integration, 'deprecated') && (
                    <PauseFormTextField
                      name="channelName"
                      fieldProps={{
                        disabled: true,
                        label: 'Channel',
                      }}
                    />
                    )}
                    {!get(integration, 'deprecated') && (
                    <PauseFormAutocomplete
                      name="slackChannelID"
                      fieldProps={{
                        label: 'Slack Channel',
                        options: (slackChannels || []).map((a) => ({
                          label: a.name,
                          value: a.id,
                        })).filter((a) => !a.archived),
                      }}
                    />
                    )}
                    <PauseTypography
                      variant="body2"
                      color="pause.primary.medium"
                      css={(theme) => ({
                        marginTop: theme.spacing(-2),
                      })}
                    >
                      <Info />
                      {' '}
                      To see private channels in this list,
                      invite Pause bot to the private channel first.
                    </PauseTypography>
                    {type === 'weekly' && (
                    <PauseTypography variant="subtitle" color="pause.primary.medium">
                      Choose notification time &amp; day
                    </PauseTypography>
                    )}
                    {type === 'weekly' && (
                    <PauseFormSelect
                            // @ts-ignore
                      name="notificationDayOfWeek"
                      fieldProps={{
                        label: 'Day of week',
                        options: (daysOfWeek || []).map((a) => ({
                          label: a,
                          value: a.toUpperCase(),
                        })),
                      }}
                    />
                    )}
                    {type === 'daily' && (
                    <PauseTypography variant="subtitle" color="pause.primary.medium">
                      Choose notification time
                    </PauseTypography>
                    )}
                    <PauseFormSelect
                            // @ts-ignore
                      name="notificationTime"
                      fieldProps={{
                        label: 'Notification Time',
                        options: hours,
                      }}
                    />
                    {!integration?.deprecated ? (
                      <PauseFormField2
                        name="showLeaveType"
                        render={({ field, state }) => (
                          <PauseCheckbox {...field} {...state} checked={field.value} label="Show leave type in summary" />
                        )}
                      />
                    ) : null}
                  </div>
                </PauseModalBody>
                <PauseModalFooter>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {integration ? (
                        <PauseInteractiveButton
                          variant="outlined"
                          color="error"
                          onClick={() => onDeleteSlackIntegration(integration)}
                        >
                          Delete
                        </PauseInteractiveButton>
                      ) : (
                        <PauseButton
                          color="error"
                          variant="outlined"
                          onClick={onClose}
                        >
                          Discard
                        </PauseButton>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <PauseSubmitButton disabled={(!slackChannels.length)} enableForInitialValid>
                        <PauseButton>
                          {integration ? 'Save' : 'Create'}
                        </PauseButton>
                      </PauseSubmitButton>
                    </Grid>
                  </Grid>

                </PauseModalFooter>
              </PauseForm>
            )}

          </PauseModal>
        );
      }}
    </EditSlackIntegrationStoreWrapper>
  </Journey>

);

EditSlackIntegrationJourney.propTypes = {
};
